import { ApiOutlined, CloudUploadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Col, Row, Space } from 'antd';
import React, { useMemo, useState } from 'react';
import { withNamespaces } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import { handleApiErrors } from '../../api/axiosInstance';
import AxleAPI from '../../api/axle';
import { VEHICLE_INSURANCE_PROVIDERS } from '../../enum/VehicleInsuranceProviders';
import { selectStoreCompanySettings, useStoreSelector } from '../../utils/storeSelectors';
import InsuranceMinimumCollapsibleSection from '../../views/userdetails/insurance-info/InsuranceMinimumCollapsibleSection';
import InfoAlert from '../Alert/InfoAlert';
import WarningAlert from '../Alert/WarningAlert';
import Button from '../Button';
import SpaceSpinner from '../SpaceSpinner';
import Text from '../Text';
import Toast from '../Toast';
import className from './styles.module.scss';

const AxleIframeSection = React.lazy(() => import('./AxleIframeSection'));
const ManualInsuranceContent = React.lazy(() => import('./ManualInsuranceContent'));
const VehicleInsuranceContent = React.lazy(() => import('./VehicleInsuranceContent'));

const UserInsuranceSettingFormContent = props => {
  const {
    t,
    canUpdateInsuranceDoc,
    selectedVehicleInsurance,
    user,
    disabled,
    userComplianceDetails,
    insuranceCoverages,
    hasCanadianFavrRate,
    ...rest
  } = props;

  const queryClient = useQueryClient();
  const companySettings = useStoreSelector(selectStoreCompanySettings);
  const [axleIgnitionData, setAxleIgnitionData] = useState({
    ignitionToken: null,
    ignitionUri: null,
  });
  const [isExchangingToken, setIsExchangingToken] = useState(false);
  const [isManualInsuranceEnabled, setIsManualInsuranceEnabled] = useState(false);

  const createAxleClientMutation = useMutation(
    () => {
      setIsManualInsuranceEnabled(false);

      return AxleAPI.startIgnition();
    },
    {
      onSuccess: data => {
        setAxleIgnitionData(data);
      },
      onError: error => handleApiErrors(error.response),
    },
  );

  const exchangeTokenMutation = useMutation(
    ({ authCode, ignitionResult, ignitionToken }) => {
      setIsExchangingToken(true);
      return AxleAPI.exchangeToken({
        authCode,
        ignitionToken,
        ignitionResult,
      });
    },
    {
      onSuccess: () => {
        setIsExchangingToken(false);
        queryClient.resetQueries(['fetchSelectedVehicleInsurance', user._id], {
          exact: false,
          active: true,
        });
        queryClient.resetQueries(['fetchVehicleInsurances', user._id], {
          exact: false,
          active: true,
        });

        Toast({
          type: 'success',
          message: t('insuranceInfoSuccessfullyProcessed'),
        });
      },
      onError: error => {
        setIsExchangingToken(false);
        handleApiErrors(error.response);

        Toast({
          type: 'error',
          message: t('errorProcessingInsuranceInfo'),
        });
      },
    },
  );

  const isAxleWorkflowInProgress =
    createAxleClientMutation.isLoading || isExchangingToken || !!axleIgnitionData.ignitionToken;

  const activeProvider = useMemo(() => {
    if (isManualInsuranceEnabled) return VEHICLE_INSURANCE_PROVIDERS.MANUAL;
    if (selectedVehicleInsurance?.provider) return selectedVehicleInsurance.provider;
    return companySettings.axleIntegration
      ? VEHICLE_INSURANCE_PROVIDERS.AXLE
      : VEHICLE_INSURANCE_PROVIDERS.MANUAL;
  }, [
    companySettings.axleIntegration,
    isManualInsuranceEnabled,
    selectedVehicleInsurance?.provider,
  ]);

  const providerTitle = useMemo(() => {
    switch (activeProvider) {
      case VEHICLE_INSURANCE_PROVIDERS.AXLE:
        return selectedVehicleInsurance ? t('connectedViaAxle') : t('axleInsurance');
      case VEHICLE_INSURANCE_PROVIDERS.MEASURE_ONE:
        return t('connectedViaMeasureOne');
      default:
        return t('manualUpload');
    }
  }, [activeProvider, selectedVehicleInsurance, t]);

  const ProviderIcon =
    activeProvider === VEHICLE_INSURANCE_PROVIDERS.AXLE
      ? ApiOutlined
      : activeProvider === VEHICLE_INSURANCE_PROVIDERS.MEASURE_ONE
      ? SafetyCertificateOutlined
      : CloudUploadOutlined;

  const canUploadManually =
    activeProvider === VEHICLE_INSURANCE_PROVIDERS.MANUAL || !companySettings.axleIntegration;
  const canSwitchToManualUpload =
    companySettings.axleIntegration &&
    activeProvider !== VEHICLE_INSURANCE_PROVIDERS.MANUAL &&
    hasCanadianFavrRate;

  return (
    <Space direction="vertical" size="large" {...rest}>
      {!isAxleWorkflowInProgress && (
        <InsuranceMinimumCollapsibleSection t={t} insuranceCoverages={insuranceCoverages} />
      )}

      {createAxleClientMutation.isLoading && (
        <div className={className.iframeContainer}>
          <div className={className.spinnerContainer}>
            <SpaceSpinner />
          </div>
        </div>
      )}

      {isExchangingToken && (
        <div className={className.iframeContainer}>
          <div className={className.spinnerContainer}>
            <Space direction="vertical" align="center" size="large">
              <SpaceSpinner />
              <Text strong size="lg" textAlign="center">
                {t('retrievingInsuranceInformations')}
              </Text>
              <Text textAlign="center">{t('pleaseDoNotCloseWindow')}</Text>
              <Text type="secondary" textAlign="center">
                {t('thisProcessMayTakeUpTo')}
              </Text>
            </Space>
          </div>
        </div>
      )}

      {axleIgnitionData && axleIgnitionData.ignitionToken && (
        <AxleIframeSection
          {...axleIgnitionData}
          onError={errorMessage => {
            setAxleIgnitionData({
              ignitionToken: null,
              ignitionUri: null,
            });

            Toast({
              type: 'error',
              message: t(errorMessage),
            });
          }}
          onSuccess={({ authCode, ignitionResult }) => {
            exchangeTokenMutation.mutate({
              authCode,
              ignitionResult,
              ignitionToken: axleIgnitionData.ignitionToken,
            });
            setAxleIgnitionData({
              ignitionToken: null,
              ignitionUri: null,
            });
            setIsManualInsuranceEnabled(false);
          }}
          onExit={() => {
            setAxleIgnitionData({
              ignitionToken: null,
              ignitionUri: null,
            });
          }}
        />
      )}

      {!isAxleWorkflowInProgress && (
        <div>
          <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
            <Col>
              <Space align="center">
                <ProviderIcon style={{ fontSize: 18 }} />
                <strong>{providerTitle}</strong>
              </Space>
            </Col>
            {!disabled && (
              <Col>
                <Space wrap>
                  {canUploadManually && (
                    <ManualInsuranceContent
                      t={t}
                      user={user}
                      disabled={disabled}
                      canUpdateInsuranceDoc={canUpdateInsuranceDoc}
                      onCancel={
                        isManualInsuranceEnabled
                          ? () => setIsManualInsuranceEnabled(false)
                          : undefined
                      }
                    />
                  )}

                  {companySettings.axleIntegration && (
                    <Button
                      size="sm"
                      loading={createAxleClientMutation.isLoading}
                      disabled={createAxleClientMutation.isLoading}
                      onClick={createAxleClientMutation.mutateAsync}
                    >
                      {t('linkInsuranceAccountWithAxle')}
                    </Button>
                  )}

                  {canSwitchToManualUpload && (
                    <Button
                      size="sm"
                      type="secondary"
                      disabled={createAxleClientMutation.isLoading}
                      onClick={() => setIsManualInsuranceEnabled(true)}
                    >
                      {t('uploadInsuranceDocumentManually')}
                    </Button>
                  )}
                </Space>
              </Col>
            )}
          </Row>

          {!selectedVehicleInsurance &&
            disabled &&
            activeProvider === VEHICLE_INSURANCE_PROVIDERS.AXLE && (
              <WarningAlert closable={false} fullWidth title={t('axleInsuranceNotLinked')} />
            )}

          {!selectedVehicleInsurance &&
            !(disabled && activeProvider === VEHICLE_INSURANCE_PROVIDERS.AXLE) && (
              <InfoAlert
                closable={false}
                fullWidth
                title={t('noSelectedInsurancePolicy')}
                message={t('noSelectedInsurancePolicyDescription')}
              />
            )}

          <VehicleInsuranceContent
            t={t}
            user={user}
            selectedVehicleInsurance={selectedVehicleInsurance}
            userComplianceDetails={userComplianceDetails}
            canUpdateInsuranceDoc={canUpdateInsuranceDoc}
          />
        </div>
      )}
    </Space>
  );
};

export default withNamespaces()(UserInsuranceSettingFormContent);
